import React, { useState, useEffect } from 'react'
import { Button } from '../../../Button'
import { cn } from '../../../../../lib/utils'
import { Icon, iconTypes } from '../../../Icon'

export interface StagedMessageProps {
  title?: string
  message: string
  onSend: (message: string) => void | Promise<void>
  onReject: () => void | Promise<void>
  onEdit: (message: string) => void | Promise<void>
  isDisabled?: boolean
  isLoading?: boolean
  className?: string
}

export default function StagedMessage(props: StagedMessageProps) {
  const {
    title,
    message,
    onSend,
    onReject,
    onEdit,
    isDisabled = false,
    isLoading = false,
    className,
  } = props

  const [isEditing, setIsEditing] = useState(false)
  const [editedMessage, setEditedMessage] = useState(message)

  // Update editedMessage when message prop changes
  useEffect(() => {
    setEditedMessage(message)
  }, [message])

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setEditedMessage(newValue)
    // Auto-save changes as user types
    onEdit(newValue)
  }

  const handleSend = async () => {
    await onSend(editedMessage)
  }

  const handleReject = async () => {
    await onReject()
  }

  return (
    <div
      className={cn(
        'w-full rounded-lg bg-muted/50 border border-dashed border-muted-foreground/20 p-4 space-y-3',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon
            name="MessageSquare"
            className="h-4 w-4 text-muted-foreground"
          />
          <span className="text-sm font-medium text-muted-foreground">
            Staged Message
          </span>
        </div>
        {isLoading && (
          <Icon
            name="LoaderCircle"
            className="h-4 w-4 animate-spin text-muted-foreground"
          />
        )}
      </div>

      {/* Message Content */}
      <div className="space-y-2">
        {isEditing ? (
          <textarea
            value={editedMessage}
            onChange={handleMessageChange}
            className="w-full min-h-[80px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Edit your message..."
            disabled={isDisabled || isLoading}
            autoFocus
          />
        ) : (
          <div
            className="p-3 rounded-md bg-background/50 text-sm whitespace-pre-wrap cursor-pointer hover:bg-background/70 transition-colors"
            onClick={handleEdit}
          >
            {message}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          handleClick={handleReject}
          isDisabled={isDisabled || isLoading}
          label="Reject"
          leftIcon="X"
        />
        <Button
          variant="default"
          size="sm"
          handleClick={handleSend}
          isDisabled={isDisabled || isLoading}
          label="Send"
          leftIcon="Send"
        />
      </div>
    </div>
  )
}
